import { YES_NO, parcelsField } from "modules/ebiForms/common";
import { EbiCondition, IEbiField, IEbiFormAttachment, IEbiFormSection } from "modules/ebiForms/typings";

// Bloki wspólne pakietu „Komunikacja i budownictwo” (F1–F8, E11, E12): dane pojazdu i współwłasność (wydział
// komunikacji) oraz inwestor, nieruchomość i oświadczenie PB-5 z formularzy GUNB (architektura i nadzór budowlany).

/** Iloczyn warunków (wszystkie muszą być spełnione). */
export const every = (...conditions: EbiCondition[]): EbiCondition => ({ all: conditions });

/** Iloczyn warunków z pominięciem pustych — wygodny przy sekcjach zależnych od wariantu. */
export const allOf = (...conditions: (EbiCondition | undefined)[]): EbiCondition | undefined => {
  const list = conditions.filter((c): c is EbiCondition => !!c);
  if (list.length === 0) return undefined;
  return list.length === 1 ? list[0] : { all: list };
};

export const PRD = "ustawy z dnia 20 czerwca 1997 r. — Prawo o ruchu drogowym";
export const PB = "ustawy z dnia 7 lipca 1994 r. — Prawo budowlane";
export const REG_1709 =
  "rozporządzenia Ministra Infrastruktury z dnia 8 listopada 2024 r. w sprawie rejestracji i oznaczania pojazdów (Dz. U. poz. 1709)";

export const VEHICLE_ORGAN = "Starosta (prezydent miasta na prawach powiatu) — wydział komunikacji";
export const ARCHITECTURE_ORGAN =
  "Starosta (prezydent miasta na prawach powiatu) jako organ administracji architektoniczno-budowlanej";
export const SUPERVISION_ORGAN = "Powiatowy inspektor nadzoru budowlanego";

export const KPA_DEADLINE = "Do miesiąca, a w sprawach szczególnie skomplikowanych do dwóch miesięcy (art. 35 § 3 KPA).";

// ── pojazdy ──────────────────────────────────────────────────────────────────

/** Dane pojazdu jak w załącznikach nr 1 i 8 do rozporządzenia o rejestracji (pkt 1–5). */
export const vehicleFields = (
  options: { full?: boolean; registrationLabel?: string; registrationVisibleWhen?: EbiCondition } = {},
): IEbiField[] => {
  const kind: IEbiField = {
    code: "pojazdRodzaj",
    type: "text",
    label: "Rodzaj pojazdu i przeznaczenie",
    required: true,
    width: "half",
    maxLength: 100,
    placeholder: "np. samochód osobowy",
  };
  const year: IEbiField = { code: "pojazdRok", type: "number", label: "Rok produkcji", required: true, width: "half", min: 1900, max: 2100 };
  const make: IEbiField = { code: "pojazdMarka", type: "text", label: "Marka, typ, model", required: true, width: "half", maxLength: 100 };
  const vin: IEbiField = {
    code: "pojazdVin",
    type: "text",
    label: "Numer VIN albo numer nadwozia, podwozia lub ramy",
    required: true,
    width: "half",
    maxLength: 30,
  };
  const registration: IEbiField = {
    code: "pojazdNrRej",
    type: "text",
    label: options.registrationLabel ?? "Numer rejestracyjny",
    required: true,
    visibleWhen: options.registrationVisibleWhen,
    width: "half",
    maxLength: 12,
    placeholder: "np. WA 12345",
  };

  return options.full ? [kind, make, year, vin, registration] : [make, vin, registration];
};

export const CO_OWNED: EbiCondition = { field: "wspolwlasnosc", equals: "tak" };

/** Współwłaściciel działa za zgodą większości współwłaścicieli (art. 78 ust. 2a PRD, § 14 rozporządzenia o rejestracji). */
export const coOwnersFields = (label = "Czy pojazd jest przedmiotem współwłasności?", visibleWhen?: EbiCondition): IEbiField[] => [
  { code: "wspolwlasnosc", type: "radio", label, required: true, defaultValue: "nie", options: YES_NO, visibleWhen },
  {
    code: "zgodaWspolwlascicieli",
    type: "declaration",
    label: "Oświadczam, że działam za zgodą i wiedzą większości współwłaścicieli pojazdu.",
    required: true,
    visibleWhen: allOf(visibleWhen, CO_OWNED),
  },
];

// ── budownictwo ──────────────────────────────────────────────────────────────

export const OTHER_INVESTORS: EbiCondition = { field: "kolejniInwestorzy", equals: "tak" };

/** Pozostali inwestorzy — przypis 1 do wzorów GUNB: dane kolejnych inwestorów dodaje się w formularzu. */
export const investorSection = (
  options: { title?: string; description?: string; question?: string; listLabel?: string; visibleWhen?: EbiCondition } = {},
): IEbiFormSection => ({
  code: "inwestor",
  title: options.title ?? "Inwestor",
  description:
    options.description ??
    "Inwestorem jest wnioskodawca, a gdy wniosek składa pełnomocnik — mocodawca. Dane kolejnych inwestorów dodaje się we wniosku.",
  visibleWhen: options.visibleWhen,
  fields: [
    {
      code: "kolejniInwestorzy",
      type: "radio",
      label: options.question ?? "Czy w sprawie występuje więcej niż jeden inwestor?",
      required: true,
      defaultValue: "nie",
      options: YES_NO,
    },
    {
      code: "inwestorzy",
      type: "repeat",
      label: options.listLabel ?? "Pozostali inwestorzy",
      itemLabel: "Inwestor",
      required: true,
      min: 1,
      max: 10,
      visibleWhen: OTHER_INVESTORS,
      fields: [
        { code: "nazwa", type: "text", label: "Imię i nazwisko lub nazwa", required: true, maxLength: 300 },
        { code: "adres", type: "text", label: "Adres — ulica, numer, kod pocztowy, miejscowość", required: true, maxLength: 300 },
      ],
    },
  ],
});

/** Dane nieruchomości z formularzy GUNB (gmina, miejscowość, adres, działki ewidencyjne). */
export const propertySection = (
  title = "Dane nieruchomości (miejsce wykonywania robót budowlanych)",
  visibleWhen?: EbiCondition,
): IEbiFormSection => ({
  code: "nieruchomosc",
  title,
  visibleWhen,
  fields: [
    { code: "nieruchomoscGmina", type: "text", label: "Gmina", required: true, width: "half", maxLength: 100 },
    { code: "nieruchomoscMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
    { code: "nieruchomoscUlica", type: "text", label: "Ulica i numer domu (jeśli nadano)", width: "twoThirds", maxLength: 200 },
    { code: "nieruchomoscKodPocztowy", type: "postalCode", label: "Kod pocztowy", width: "third", placeholder: "00-000" },
    {
      ...parcelsField(),
      hint: "Obręb ewidencyjny i numer działki — jak w ewidencji gruntów i budynków (zamiast identyfikatora działki ewidencyjnej).",
    },
  ],
});

/**
 * Oświadczenie o posiadanym prawie do dysponowania nieruchomością na cele budowlane — treść wzoru PB-5 (art. 32 ust. 4
 * pkt 2 Pb: oświadczenie pod rygorem odpowiedzialności karnej). Składa je osoba podpisująca wniosek; pozostali inwestorzy
 * dołączają własne formularze PB-5 (`rightToDisposeAttachment`).
 */
export const rightToDisposeSection = (visibleWhen?: EbiCondition): IEbiFormSection => ({
  code: "pb5",
  title: "Oświadczenie o posiadanym prawie do dysponowania nieruchomością na cele budowlane (PB-5)",
  description:
    "Prawo do dysponowania nieruchomością na cele budowlane to tytuł prawny wynikający z prawa własności, użytkowania wieczystego, zarządu, ograniczonego prawa rzeczowego albo stosunku zobowiązaniowego, przewidującego uprawnienia do wykonywania robót budowlanych. Oświadczenie składa inwestor albo osoba upoważniona do złożenia go w imieniu inwestora.",
  visibleWhen,
  fields: [
    {
      code: "pb5Oswiadczenie",
      type: "declaration",
      label:
        "Po zapoznaniu się z art. 32 ust. 4 pkt 2 oraz art. 3 pkt 11 ustawy z dnia 7 lipca 1994 r. — Prawo budowlane oświadczam, że posiadam prawo do dysponowania nieruchomością (nieruchomościami) na cele budowlane określoną (określonymi) we wniosku.",
      required: true,
    },
    {
      code: "pb5Klauzula",
      type: "declaration",
      label:
        "Jestem świadomy (świadoma) odpowiedzialności karnej za podanie nieprawdy w niniejszym oświadczeniu, zgodnie z art. 233 ustawy z dnia 6 czerwca 1997 r. — Kodeks karny.",
      required: true,
    },
  ],
});

export const rightToDisposeAttachment = (visibleWhen?: EbiCondition): IEbiFormAttachment => ({
  code: "pb5Pozostali",
  label: "Oświadczenia pozostałych inwestorów o posiadanym prawie do dysponowania nieruchomością na cele budowlane (PB-5)",
  hint: "Każdy inwestor składa oświadczenie oddzielnie, na formularzu PB-5.",
  multiple: true,
  required: true,
  visibleWhen: allOf(visibleWhen, OTHER_INVESTORS),
});

export const NO_LOCAL_PLAN: EbiCondition = { field: "mpzp", equals: "nie" };

export const localPlanField = (visibleWhen?: EbiCondition): IEbiField => ({
  code: "mpzp",
  type: "radio",
  label: "Czy dla terenu inwestycji obowiązuje miejscowy plan zagospodarowania przestrzennego?",
  required: true,
  options: YES_NO,
  visibleWhen,
});

export const zoningDecisionAttachment = (visibleWhen?: EbiCondition): IEbiFormAttachment => ({
  code: "decyzjaWz",
  label: "Decyzja o warunkach zabudowy i zagospodarowania terenu",
  hint: "Jeżeli jest wymagana zgodnie z przepisami o planowaniu i zagospodarowaniu przestrzennym; zamiast oryginału można dołączyć kopię.",
  required: true,
  visibleWhen: allOf(visibleWhen, NO_LOCAL_PLAN),
});

export const otherLawsAttachment = (hint: string, visibleWhen?: EbiCondition): IEbiFormAttachment => ({
  code: "pozwoleniaOdrebne",
  label: "Pozwolenia, uzgodnienia, opinie i inne dokumenty wymagane przepisami odrębnych ustaw",
  hint,
  multiple: true,
  visibleWhen,
});

export const WAIVED: EbiCondition = { field: "zwolnienie", equals: true };

/** Zwolnienie z opłaty skarbowej (najczęściej: sprawy budownictwa mieszkaniowego) — `exemptWhen` do opłat i dowodu wpłaty. */
export const stampDutyExemptionFields = (visibleWhen?: EbiCondition): IEbiField[] => [
  {
    code: "zwolnienie",
    type: "checkbox",
    label:
      "Sprawa jest zwolniona z opłaty skarbowej (np. sprawy budownictwa mieszkaniowego — art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej)",
    visibleWhen,
  },
  {
    code: "podstawaZwolnienia",
    type: "text",
    label: "Podstawa zwolnienia",
    required: true,
    visibleWhen: allOf(visibleWhen, WAIVED),
    maxLength: 300,
    placeholder: "np. budowa budynku mieszkalnego jednorodzinnego",
  },
];

export const exemptWhen = (visibleWhen?: EbiCondition): EbiCondition => allOf(visibleWhen, WAIVED) ?? WAIVED;
